import { useRef } from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "@/mocks/homeData";
import { useTranslation } from "react-i18next";

export default function BlogShowcase() {
  const { t, i18n } = useTranslation();
  const localeMap: Record<string, string> = { en: "en-GB", fr: "fr-FR", de: "de-DE" };
  const dateLocale = localeMap[i18n.language] || "en-GB";
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const amount = 300;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -amount : amount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="bg-background-950 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 mb-8 md:mb-12">
        <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 text-center">
          <span className="px-4 py-1.5 border border-primary-400/30 text-primary-400 text-xs md:text-sm font-medium rounded-full whitespace-nowrap">
            {t("blog.badge")}
          </span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 text-center">
            {t("blog.heading")}
          </h2>
        </div>
      </div>

      <div className="relative md:max-w-5xl md:mx-auto md:px-6">
        {/* Mobile arrows only */}
        <button
          onClick={() => scroll("left")}
          className="md:hidden absolute left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-900/80 backdrop-blur-sm border border-background-700 text-background-50 hover:bg-background-800 transition-colors cursor-pointer shadow-lg"
          aria-label="Scroll left"
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </button>

        <div
          ref={scrollRef}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 overflow-x-auto md:overflow-visible px-12 md:px-0 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {blogPosts.map((post) => {
            const title = t(`posts.${post.key}.title`);
            return (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="flex-shrink-0 w-[260px] md:w-auto cursor-pointer group snap-start"
              >
                <div className="bg-background-800 rounded-lg overflow-hidden">
                  <div className="w-full aspect-square overflow-hidden">
                    <img
                      src={post.image}
                      alt={title}
                      title={`${title} Clefs de Lys SA`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="mt-3 md:mt-4">
                  <span className="text-xs text-primary-400 font-medium uppercase tracking-wider">
                    {t(`posts.${post.key}.category`)}
                  </span>
                  <h3 className="text-sm md:text-base font-semibold text-background-50 mt-1 line-clamp-2 group-hover:text-primary-400 transition-colors">
                    {title}
                  </h3>
                  <p className="text-xs text-background-50/50 mt-1">
                    {new Date(post.date).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Mobile arrows only */}
        <button
          onClick={() => scroll("right")}
          className="md:hidden absolute right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-900/80 backdrop-blur-sm border border-background-700 text-background-50 hover:bg-background-800 transition-colors cursor-pointer shadow-lg"
          aria-label="Scroll right"
        >
          <i className="ri-arrow-right-line text-lg"></i>
        </button>
      </div>
    </section>
  );
}